import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useApi } from "@/lib/api";
import { PageHeader } from "@/components/portal/StatusBadge";
import { GoogleCalendarCard } from "@/components/portal/GoogleCalendarCard";
import { CalendarFeedCard } from "@/components/portal/CalendarFeedCard";
import { useMe } from "@/components/portal/MeContext";
import { markSeen } from "@/components/portal/WelcomeChecklist";

const COLORS = {
  project_start: "bg-[#0E1F3F] text-white",
  project_end: "bg-green-600 text-white",
  task: "bg-[#5C6B85] text-white",
  appointment: "bg-[#F2B91C] text-[#0E1F3F]",
};
const LEGEND = [["project_start", "Project start"], ["project_end", "Target finish"], ["task", "Task due"], ["appointment", "Estimate visit"]];
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const EventChip = ({ e }) => {
  const body = <span className={`block truncate rounded px-1.5 py-0.5 text-[11px] font-semibold ${COLORS[e.type]} ${e.status === "requested" ? "opacity-70 ring-1 ring-dashed ring-[#0E1F3F]" : ""}`}>{e.time ? `${e.time} ` : ""}{e.title}</span>;
  return e.link ? <Link to={e.link} title={e.title} data-testid={`cal-event-${e.id}`}>{body}</Link> : <span title={e.title} data-testid={`cal-event-${e.id}`}>{body}</span>;
};

export default function CalendarPage() {
  const api = useApi();
  const me = useMe();
  const [events, setEvents] = useState([]);
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  useEffect(() => {
    api("get", "/calendar").then(setEvents);
    if (me.role === "employee") markSeen(api, "calendar");
  }, [api, me.role]);

  const days = useMemo(() => {
    const start = new Date(month);
    start.setDate(1 - start.getDay());
    return Array.from({ length: 42 }, (_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
  }, [month]);
  const byDay = useMemo(() => events.reduce((m, e) => ({ ...m, [e.date]: [...(m[e.date] || []), e] }), {}), [events]);
  const shift = (n) => setMonth(new Date(month.getFullYear(), month.getMonth() + n, 1));
  const todayIso = iso(new Date());
  const upcoming = events.filter((e) => e.date >= todayIso).slice(0, 8);

  return (
    <div data-testid="calendar-page">
      <PageHeader eyebrow="Schedule" title="Calendar" />
      {me.role === "admin" && <><CalendarFeedCard /><GoogleCalendarCard /></>}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button onClick={() => shift(-1)} data-testid="cal-prev" className="grid h-9 w-9 place-items-center rounded-full border bg-white"><ChevronLeft size={16} /></button>
          <p className="font-display w-44 text-center text-lg font-bold" data-testid="cal-month">{month.toLocaleDateString("en-US", { month: "long", year: "numeric" })}</p>
          <button onClick={() => shift(1)} data-testid="cal-next" className="grid h-9 w-9 place-items-center rounded-full border bg-white"><ChevronRight size={16} /></button>
        </div>
        <div className="flex flex-wrap gap-3 text-xs">{LEGEND.map(([k, l]) => <span key={k} className="flex items-center gap-1.5"><span className={`h-3 w-3 rounded ${COLORS[k].split(" ")[0]}`} />{l}</span>)}</div>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-[#E6E2DA] bg-white">
        <div className="grid min-w-[720px] grid-cols-7">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d} className="border-b p-2 text-center text-xs font-semibold text-[#8C877D]">{d}</div>)}
          {days.map((d) => {
            const k = iso(d);
            const out = d.getMonth() !== month.getMonth();
            return (
              <div key={k} data-testid={`cal-day-${k}`} className={`min-h-[110px] space-y-1 border-b border-r p-1.5 ${out ? "bg-[#FAF8F5] text-[#C9C4BA]" : ""}`}>
                <p className={`text-xs font-semibold ${k === todayIso ? "inline-grid h-6 w-6 place-items-center rounded-full bg-[#F2B91C] text-[#0E1F3F]" : ""}`}>{d.getDate()}</p>
                {(byDay[k] || []).slice(0, 4).map((e) => <EventChip key={e.id} e={e} />)}
                {(byDay[k] || []).length > 4 && <p className="text-[10px] text-[#8C877D]">+{byDay[k].length - 4} more</p>}
              </div>
            );
          })}
        </div>
      </div>
      <div className="mt-8 rounded-2xl border border-[#E6E2DA] bg-white p-5" data-testid="cal-upcoming">
        <p className="eyebrow mb-3">Coming up</p>
        {!upcoming.length && <p className="text-sm text-[#5C5850]">Nothing scheduled yet.</p>}
        {upcoming.map((e) => (
          <div key={e.id} className="flex items-center justify-between border-b py-2 text-sm last:border-0">
            <span className="font-medium">{e.title}{e.status === "requested" ? " (awaiting confirmation)" : ""}</span>
            <span className="font-mono-num text-xs text-[#5C5850]">{new Date(`${e.date}T00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" })}{e.time ? ` · ${e.time}` : ""}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
